<template>
  <div class="mission-questions">
    <v-card class="elevation-0" ref="container">
      <!--
        Disabled use of scrollableHeight because it is resolving to a very small
        value (e.g. 32px) on Safari / iOS mobile app, making div size impractical.
      -->
      <!-- <v-card-text :style="`height: ${scrollableHeight}px`"> -->
      <v-card-text>
        <v-stepper class="elevation-0" v-model="step">
          <v-stepper-items>
            <v-stepper-content
              v-for="question in questions"
              :key="question.code"
              :step="question.code"
            >
              <p class="text-h6 text--primary">
                {{ question.name }}
              </p>
              <v-row>
                <v-col
                  v-for="answer in question.answers"
                  :key="`${question.code}--${answer.code}`"
                  cols="3"
                >
                  <v-btn
                    light
                    :color="isAnswerChosen(question, answer) ? 'success' : ''"
                    block
                    x-large
                    class="btn-height-2x"
                    @click="() => chooseAnswer(question, answer)"
                  >
                    {{ answer.name }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions ref="footer">
        <v-row>
          <v-col>
            <v-btn
              block
              light
              x-large
              :disabled="stepHistory.length === 0"
              @click="back"
            >
              BACK
            </v-btn>
          </v-col>
          <v-col>
            <v-btn block light x-large @click="cancel">
              CANCEL
            </v-btn>
          </v-col>
          <v-col>
            <!--
              NOTE: We use the mission type and mission ID here because
              the mission_asset will not exist yet. The API can then
              re-link the files to the mission_asset when it gets
              the array of uploaded file_ids on mission create.
            -->
            <file-picker-button
              type="mission"
              :type-id="missionId"
              @uploaded="file => this.form.files.push(file.id)"
              @upload-start="() => $store.commit('startLoading')"
              @upload-end="() => $store.commit('endLoading')"
            >
              <template #default="{ on, loading }">
                <v-btn v-on="on" :loading="loading" light x-large block>
                  PHOTO
                </v-btn>
              </template>
            </file-picker-button>
          </v-col>
          <v-col>
            <v-btn
              block
              x-large
              light
              :disabled="false"
              @click.prevent="showCommentDialog = true"
            >
              Comment
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              v-if="isNextStepEnd"
              block
              x-large
              light
              :disabled="!hasAnswers(currentQuestion)"
              key="finish-button"
              @click="finish"
            >
              FINISH
            </v-btn>
            <v-btn
              v-else
              block
              x-large
              light
              :disabled="!hasAnswers(currentQuestion)"
              key="next-button"
              @click="() => next(currentQuestion)"
            >
              NEXT
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <!-- The modal for adding a comment while progressing through questions -->
    <v-dialog
      v-model="showCommentDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title class="text-h5">
          Comment
        </v-card-title>

        <v-card-text>
          <v-textarea
            light
            solo
            class="mission-questions-comment"
            :hide-details="false"
            v-model="form.comment_text"
            persistent-hint
            hint="Comment is only saved once all questions have been answered."
            label="Enter your comment here...">
          </v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="showCommentDialog = false"
          >
            OK
          </v-btn>
          <v-btn
            color="light"
            text
            @click="showCommentDialog = false"
          >
            Cancel and Clear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FilePickerButton from "@/components/File/FilePickerButton";

export default {
  props: {
    missionId: {
      type: [Number, String],
      required: true
    },
    mission: {
      type: Object,
      required: true,
    }
  },
  components: {
    FilePickerButton
  },
  data: () => ({
    form: {
      comment_text: '',
      questions: [],
      files: [],
    },
    step: "",
    stepHistory: [],
    scrollableHeight: 0,
    showCommentDialog: false
  }),
  computed: {
    ...mapGetters("config", ["getConfigByKey"]),
    questions() {
      // return this.getConfigByKey("inspection_criteria") || [];
      const questions = (
        this.mission
        && this.mission.config
        && this.mission.config.inspection_criteria
        )
          ? this.mission.config.inspection_criteria
          : this.getConfigByKey('inspection_criteria');
      return questions || [];
    },
    currentQuestion() {
      return this.questions.find(question => question.code === this.step);
    },
    isNextStepEnd() {
      const nextStep = this.getNextStep(this.currentQuestion);
      return nextStep === "END";
    }
  },
  created() {
    this.step = this.questions[0].code;
  },
  mounted() {
    this.setScrollableHeight();
  },
  methods: {
    setScrollableHeight() {
      const containerHeight = this.$refs.container.$el.clientHeight;
      const footerHeight = this.$refs.footer.clientHeight;

      this.scrollableHeight = containerHeight - footerHeight;
    },
    getFormQuestion(question) {
      return this.form.questions.find(
        formQuestion => formQuestion.code === question.code
      );
    },
    getFormAnswers(question) {
      const formQuestion = this.getFormQuestion(question);
      return formQuestion ? formQuestion.answers : [];
    },
    hasAnswers(question) {
      if (question.mandatory) {
        return this.getFormAnswers(question).length;
      }
      return true;
    },
    isAnswerChosen(question, answer) {
      return this.getFormAnswers(question).find(
        formAnswer => formAnswer.code === answer.code
      );
    },
    commitAnswers() {
      // We call this method inside next() and finish() for cases when a user
      // deliberately chooses no answer so we can still record that choice.
      // Otherwise, chooseAnswer() is never fired and we have no record
      // that the question was asked but then skipped by the user.
      // This is a temporary solution until it becomes possible
      // to deliberately pick null as an answer to questions.
      // (e.g. selecting zero checkboxes *is* your answer)
      const currentQuestion = this.currentQuestion;
      const currentAnswers = this.getFormAnswers(currentQuestion);
      if ( ! currentAnswers.length) {
        this.chooseAnswer(currentQuestion, null);
      }
    },
    chooseAnswer(question, answer) {
      let formQuestion = this.getFormQuestion(question);

      if (!formQuestion) {
        this.form.questions.push({
          ...question,
          answers: []
        });

        formQuestion = this.form.questions[this.form.questions.length - 1];
      }

      // By now we've recorded that the question was asked by adding
      // it to the form.questions array (above). So in cases where
      // a user is skipping an optional answer, we can stop here.
      if ( ! answer) return;

      switch (question.type) {
        // Questions that requires only one answer
        case "radio":
          formQuestion.answers = [];
          formQuestion.answers.push(answer);

          // Go to next question
          if (this.getNextStep(question) === "END") {
            this.finish();
          } else {
            this.next(question);
          }

          break;
        // Questions that requires multiple answers
        case "checkbox":
          const formAnswerIndex = formQuestion.answers.findIndex(
            formAnswer => formAnswer.code === answer.code
          );

          // Toogle answer
          if (formAnswerIndex > -1) {
            formQuestion.answers.splice(formAnswerIndex, 1);
          } else {
            formQuestion.answers.push(answer);
          }

          break;
      }
    },
    getNextStep(question) {
      let nextStep = null;

      switch (question.type) {
        // Next step is attached in the answers
        case "radio":
          if (this.hasAnswers(question)) {
            const formAnswers = this.getFormAnswers(question);
            nextStep = formAnswers[0].next_step;
          }
          break;
        // Next step is attached in the question
        case "checkbox":
          nextStep = question.next_step;
          break;
      }

      return nextStep;
    },
    cancel() {
      this.$emit("cancel");
    },
    back() {
      const prevStep = this.stepHistory.pop();

      if (prevStep) {
        this.step = prevStep;
      }
    },
    next(question) {
      this.commitAnswers();
      let nextStep = this.getNextStep(question);

      if (nextStep) {
        this.stepHistory.push(this.step);
        this.step = nextStep;
      }

      this.setScrollableHeight();
    },
    finish() {
      this.commitAnswers();
      this.$emit("finish", this.form);
    }
  }
};
</script>

<style lang="scss">
.mission-questions {
  height: 100%;
  .v-card,
  .v-stepper {
    height: 100%;
  }
  .v-stepper__items {
    height: 100%;
    overflow-y: auto;
  }
  .btn-height-2x {
    height: 104px !important;
  }
}
.v-card.theme--dark .mission-questions-comment.theme--light .v-messages.theme--light {
  color: #eee;
}
.v-btn__content {
  flex: initial;
  white-space: normal !important;
}
</style>
